import React, { useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import useCommissionCatalog from "@/hooks/useCommissionCatalog";
import { pickQuoteRowValue } from "@/pages/appointmentDetails/helpers";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INSURANCE_TYPES = ["Annuity", "Term Life", "Universal Life", "Whole Life"];
const ENROLLMENT_TYPES = ["Underwritten", "Guaranteed Issue"];
const YES_NO = ["Yes", "No"];
const COVERAGE_LENGTH_OPTIONS = [
  "N/A",
  "3 Years",
  "5 Year",
  "7 Years",
  "10 Years",
  "14 Years",
  "15 Years",
  "20 Years",
  "25 Years",
  "30 Years",
];
const SPLIT_PERCENT_OPTIONS = ["10", "20", "30", "40", "50", "60", "70", "80", "90"];

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const buildFieldsForInsuranceType = (insuranceType, options = {}) => {
  const { companyOptions = [], productOptions = [], issueAgeOptions = [], profilesLoading = false } = options;
  const base = [
    { key: "application_date", label: "Application Date", type: "date" },
    { key: "sales_agent", label: "Sales Agent", type: "sales_agent" },
    { key: "sold_option_index", label: "Sold Quote Option", type: "buttons", options: [1, 2, 3] },
    { key: "insurance_type", label: "Insurance Type", type: "buttons", options: INSURANCE_TYPES },
    { key: "enrollment_type", label: "Enrollment Type", type: "buttons", options: ENROLLMENT_TYPES },
    { key: "company", label: "Company", type: "dropdown", options: companyOptions, forceDropdown: true },
    { key: "product_name", label: "Product Name", type: "dropdown", options: productOptions, forceDropdown: true },
    {
      key: "issue_age",
      label: "Issue Age",
      type: "dropdown",
      options: issueAgeOptions,
      placeholder: profilesLoading ? "Loading issue ages..." : "Select issue age",
      forceDropdown: true,
    },
    { key: "coverage_length", label: "Coverage Length", type: "dropdown", options: COVERAGE_LENGTH_OPTIONS },
  ];

  const premiumFields = (() => {
    if (insuranceType === "Universal Life") {
      return [
        { key: "annual_target_premium", label: "Annual Target Premium", type: "currency" },
        { key: "monthly_premium", label: "Monthly Premium", type: "currency" },
        { key: "death_benefit", label: "Death Benefit", type: "currency" },
      ];
    }
    if (insuranceType === "Term Life" || insuranceType === "Whole Life") {
      return [
        { key: "monthly_premium", label: "Monthly Premium", type: "currency" },
        { key: "death_benefit", label: "Death Benefit", type: "currency" },
      ];
    }
    if (insuranceType === "Annuity") {
      return [{ key: "annual_premium", label: "Annual Premium", type: "currency" }];
    }
    return [];
  })();

  const tail = [
    { key: "rider_added", label: "Rider Added?", type: "buttons", options: YES_NO },
    { key: "added_riders", label: "Added Rider(s)", type: "text", showWhen: { key: "rider_added", value: "Yes" } },
    { key: "split_case", label: "Was this a Split Case?", type: "buttons", options: YES_NO },
    { key: "split_agent", label: "Split Agent", type: "agent_select", showWhen: { key: "split_case", value: "Yes" } },
    { key: "split_percent", label: "Split %", type: "buttons", options: SPLIT_PERCENT_OPTIONS, showWhen: { key: "split_case", value: "Yes" } },
  ];

  return [...base, ...premiumFields, ...tail];
};

const buildDefaultValues = (user, prefill) => {
  const insuranceType = INSURANCE_TYPES.includes(prefill?.insurance_type) ? prefill?.insurance_type : "";
  // P4-WP-14A: an id. `prefill.sales_agent_id` comes from the appointment's
  // `sales_agent_user_id`, so both sources are durable user ids.
  const defaultSalesAgentId = prefill?.sales_agent_id || user?.id || "";
  return {
    application_date: toDateInputValue(prefill?.application_date || prefill?.start_at),
    sales_agent: defaultSalesAgentId,
    sold_option_index: "",
    insurance_type: insuranceType,
    enrollment_type: "",
    company: prefill?.company || "",
    product_name: prefill?.product_name || "",
    coverage_length: "",
    annual_premium: "",
    annual_target_premium: "",
    monthly_premium: prefill?.monthly_premium || "",
    death_benefit: prefill?.death_benefit || "",
    rider_added: "",
    added_riders: "",
    issue_age: prefill?.issue_age ? String(prefill.issue_age) : "",
    split_case: "",
    split_agent: "",
    split_percent: "",
  };
};

const pickFirstValue = (values) => {
  if (values === null || values === undefined) return "";
  const str = String(values).trim();
  return str.length ? str : "";
};

export default function SoldDialog({ open, onOpenChange, appointmentId, onSold, prefill, quoteRef }) {
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [salesAgents, setSalesAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [commissionProfiles, setCommissionProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  const user = useMemo(() => getStoredUser(), []);
  // P4-WP-14A: decide from the SELECTED-ACCOUNT membership role.
  //
  // `user.role` is the deprecated global column. It has not decided Account
  // authority since WP-1, and the legacy literal "agent" is not an alias for
  // the canonical `sales_agent` -- so a real Sales Agent never matched the old
  // check and silently fell through to the free-text branch.
  const accountRole = user?.membership_role || null;
  const isAdmin = ["owner", "admin"].includes(accountRole);
  const isAgent = ["sales_agent", "booking_agent"].includes(accountRole);
  const canListAsSalesAgent = Boolean(user?.list_as_sales_agent);
  const showSalesAgentField = isAdmin || isAgent;
  const shouldAutoSelectAgent = isAgent || (isAdmin && canListAsSalesAgent);

  const {
    companyOptions,
    ensureProducts,
    getCompanyIdByName,
    getProductOptionsByCompanyName,
  } = useCommissionCatalog({ enabled: open, filterByProfiles: true });

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    const defaults = buildDefaultValues(user, prefill);
    if (showSalesAgentField) {
      // An ID, never a name: this value becomes an authorization-bearing
      // entry in the Client's sales_agent_user_ids array.
      defaults.sales_agent = shouldAutoSelectAgent ? user?.id || "" : "";
    }
    setFormValues(defaults);
  }, [open, prefill, user, showSalesAgentField, shouldAutoSelectAgent]);

  useEffect(() => {
    if (!open || !showSalesAgentField) return;
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        if (isAgent && user?.id) {
          setSalesAgents([{ id: user.id, name: user.name || "Sales Agent" }]);
          return;
        }
        const res = await api.get("/admin/users");
        const list = Array.isArray(res.data) ? res.data : [];
        setSalesAgents(
          list.filter(
            (u) =>
              ["agent", "booking_agent"].includes(u.role) ||
              (["admin", "super_admin"].includes(u.role) && u.list_as_sales_agent)
          )
        );
      } catch {
        setSalesAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };
    loadAgents();
  }, [open, showSalesAgentField, isAgent, user]);

  useEffect(() => {
    if (!open) return;
    const loadProfiles = async () => {
      setProfilesLoading(true);
      try {
        const res = await api.get("/commission/profiles");
        setCommissionProfiles(Array.isArray(res.data) ? res.data : []);
      } catch {
        setCommissionProfiles([]);
      } finally {
        setProfilesLoading(false);
      }
    };
    loadProfiles();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const companyId = getCompanyIdByName(formValues.company);
    if (companyId) ensureProducts(companyId);
  }, [formValues.company, getCompanyIdByName, ensureProducts, open]);

  const insuranceType = formValues.insurance_type || "";
  const productOptions = useMemo(
    () => {
      const baseOptions = getProductOptionsByCompanyName(formValues.company);
      if (baseOptions.length || !formValues.company) {
        return baseOptions;
      }
      const fallbackProducts = Array.from(
        new Set(
          commissionProfiles
            .filter((profile) => profile.company_name === formValues.company)
            .map((profile) => profile.product_name)
            .filter(Boolean)
        )
      );
      return fallbackProducts.map((name) => ({ value: name, label: name }));
    },
    [formValues.company, getProductOptionsByCompanyName, commissionProfiles]
  );

  const issueAgeOptions = useMemo(() => {
    if (!formValues.company || !formValues.product_name) return [];
    const matches = commissionProfiles.filter(
      (profile) =>
        profile.company_name === formValues.company &&
        profile.product_name === formValues.product_name
    );
    const unique = Array.from(
      new Set(
        matches
          .map((profile) => profile.issue_age)
          .filter((age) => age !== undefined && age !== null)
          .map((age) => String(age))
      )
    );
    return unique
      .sort((a, b) => Number(a) - Number(b))
      .map((age) => ({ value: age, label: age }));
  }, [commissionProfiles, formValues.company, formValues.product_name]);

  useEffect(() => {
    if (!open) return;
    if (!formValues.issue_age) return;
    if (profilesLoading) return;
    if (issueAgeOptions.length === 0) return;
    const normalized = String(formValues.issue_age);
    const optionValues = issueAgeOptions.map((option) => option.value);
    if (optionValues.includes(normalized)) return;
    setFormValues((prev) => ({ ...prev, issue_age: "" }));
  }, [open, formValues.issue_age, issueAgeOptions, profilesLoading]);

  // Auto-populate Company, Product Name, and Issue Age when sold_option_index changes
  useEffect(() => {
    if (!open) return;
    const optionIndex = formValues.sold_option_index;
    if (!optionIndex) return;
    
    const quoteRows = quoteRef?.quote_rows;
    if (!Array.isArray(quoteRows) || quoteRows.length === 0) return;
    
    const idx = Number(optionIndex);
    if (Number.isNaN(idx)) return;
    
    // Extract values from quote rows for the selected option
    const company = String(pickQuoteRowValue(quoteRows, [/company/i, /carrier/i], idx) || "").trim();
    const productName = String(pickQuoteRowValue(quoteRows, [/product/i], idx) || "").trim();
    const issueAge = String(pickQuoteRowValue(quoteRows, [/issue age/i], idx) || "").trim();
    
    // Only update fields that have values in the quote
    setFormValues((prev) => ({
      ...prev,
      ...(company && { company }),
      ...(productName && { product_name: productName }),
      ...(issueAge && { issue_age: issueAge }),
    }));
  }, [open, formValues.sold_option_index, quoteRef]);

  const fields = useMemo(
    () =>
      buildFieldsForInsuranceType(insuranceType, {
        companyOptions,
        productOptions,
        issueAgeOptions,
        profilesLoading,
      }),
    [insuranceType, companyOptions, productOptions, issueAgeOptions, profilesLoading]
  );

  const salesAgentName = useMemo(() => {
    if (!showSalesAgentField) return user?.name || "Sales Agent";
    const selectedId = formValues.sales_agent;
    if (!selectedId) return "Sales Agent";
    // Match on id only. The old `|| agent.name === selectedId` fallback existed
    // because the field held a name on some paths and an id on others; it now
    // always holds an id, and tolerating a name here would let a display string
    // masquerade as an identity.
    const match = salesAgents.find((agent) => agent.id === selectedId);
    return match?.name || match?.full_name || String(selectedId);
  }, [formValues.sales_agent, salesAgents, showSalesAgentField, user]);

  const updateValue = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const getFieldValue = (field) => {
    if (field.key === "sales_agent" && !showSalesAgentField) return user?.id || "";
    return formValues[field.key];
  };

  const shouldValidateField = (field) => {
    if (field.showWhen) {
      return formValues[field.showWhen.key] === field.showWhen.value;
    }
    return true;
  };

  const canSubmit = fields.every((field) => {
    if (!shouldValidateField(field)) return true;
    const value = pickFirstValue(getFieldValue(field));
    return value !== "";
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl flex h-[75vh] max-h-[75vh] flex-col overflow-hidden"
        data-testid="sold-dialog"
      >
        <DialogHeader>
          <DialogTitle>Sale Details</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2" data-testid="sale-details-scroll">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
            {fields.map((field) => {
              if (field.key === "sales_agent" && !showSalesAgentField) return null;

              if (field.showWhen) {
                const controlValue = formValues[field.showWhen.key];
                if (controlValue !== field.showWhen.value) return null;
              }

              const fieldValue = formValues[field.key] ?? "";
              const fieldLabel =
                field.key === "split_percent"
                  ? `${salesAgentName || "Sales Agent"} Commission Split %`
                  : field.label;
              const fieldId = `sold-field-${field.key}`;

              if (field.type === "buttons") {
                return (
                  <div key={field.key} className="space-y-2" data-testid={fieldId}>
                    <Label className="text-slate-200/80">{fieldLabel}</Label>
                    <div className="flex flex-wrap gap-2">
                      {(field.options || []).map((option) => {
                        const optionValue =
                          typeof option === "object" && option !== null
                            ? String(option.value)
                            : typeof option === "number"
                              ? String(option)
                              : String(option);
                        const optionLabel =
                          typeof option === "object" && option !== null
                            ? option.label ?? optionValue
                            : typeof option === "number"
                              ? `Option ${option}`
                              : option;
                        const isActive = String(fieldValue) === optionValue;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => updateValue(field.key, optionValue)}
                            className={`rounded-full px-3 py-1 text-xs ring-1 transition ${
                              isActive
                                ? "bg-indigo-500/30 text-indigo-100 ring-indigo-400/40"
                                : "bg-white/5 text-slate-100/80 ring-white/10 hover:bg-white/10"
                            }`}
                            data-testid={`${fieldId}-option-${String(option).replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {optionLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              if (field.type === "sales_agent") {
                return (
                  <div key={field.key} className="space-y-2" data-testid={fieldId}>
                    <Label className="text-slate-200/80">{fieldLabel}</Label>
                    <Select value={fieldValue} onValueChange={(v) => updateValue(field.key, v)}>
                      <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid={`${fieldId}-trigger`}>
                        <SelectValue placeholder={agentsLoading ? "Loading..." : "Select"} />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10" data-testid={`${fieldId}-content`}>
                        {salesAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id} data-testid={`${fieldId}-option-${agent.id}`}>
                            {agent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }

              if (field.type === "date") {
                return (
                  <div key={field.key} className="space-y-2" data-testid={fieldId}>
                    <Label className="text-slate-200/80">{fieldLabel}</Label>
                    <Input
                      value={fieldValue}
                      onChange={(e) => updateValue(field.key, e.target.value)}
                      type="date"
                      className="bg-white/5 ring-1 ring-white/10"
                      data-testid={`${fieldId}-input`}
                    />
                  </div>
                );
              }

              if (field.type === "agent_select") {
                // P4-WP-14A: the split agent is an IDENTITY, so it is chosen or
                // it is absent. The free-text <Input> that used to stand in when
                // the roster was empty is deliberately gone: typed text cannot
                // be a user id, and a Client relationship array must never
                // receive a display string. An empty roster now renders a
                // disabled control that says so, rather than silently accepting
                // a value that cannot be stored as a relationship.
                if (!salesAgents.length) {
                  return (
                    <div key={field.key} className="space-y-2" data-testid={fieldId}>
                      <Label className="text-slate-200/80">{fieldLabel}</Label>
                      <Select disabled value="">
                        <SelectTrigger
                          className="bg-white/5 ring-1 ring-white/10 opacity-60"
                          data-testid={`${fieldId}-trigger`}
                        >
                          <SelectValue
                            placeholder={agentsLoading ? "Loading..." : "No selectable agents"}
                          />
                        </SelectTrigger>
                      </Select>
                    </div>
                  );
                }
                return (
                  <div key={field.key} className="space-y-2" data-testid={fieldId}>
                    <Label className="text-slate-200/80">{fieldLabel}</Label>
                    <Select value={fieldValue} onValueChange={(v) => updateValue(field.key, v)}>
                      <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid={`${fieldId}-trigger`}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10" data-testid={`${fieldId}-content`}>
                        {salesAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id} data-testid={`${fieldId}-option-${agent.id}`}>
                            {agent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }

              if (field.type === "dropdown") {
                const options = field.options || [];
                const normalizedOptions = options.map((option) => {
                  if (typeof option === "object" && option !== null) {
                    return { value: String(option.value ?? option.label ?? ""), label: option.label ?? option.value };
                  }
                  return { value: String(option), label: option };
                });
                const isIssueAgeField = field.key === "issue_age";
                const issueAgeDisabled =
                  isIssueAgeField &&
                  (!formValues.company || !formValues.product_name || normalizedOptions.length === 0);
                const placeholder = issueAgeDisabled
                  ? profilesLoading
                    ? "Loading issue ages..."
                    : "Select company and product first"
                  : normalizedOptions.length
                    ? "Select"
                    : "No options";
                if (normalizedOptions.length || field.forceDropdown) {
                  return (
                    <div key={field.key} className="space-y-2" data-testid={fieldId}>
                      <Label className="text-slate-200/80">{fieldLabel}</Label>
                      <Select
                        value={fieldValue}
                        onValueChange={(v) => updateValue(field.key, v)}
                        disabled={field.forceDropdown && normalizedOptions.length === 0 || issueAgeDisabled}
                      >
                        <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid={`${fieldId}-trigger`}>
                          <SelectValue placeholder={placeholder} />
                        </SelectTrigger>
                        <SelectContent className="glass border-white/10" data-testid={`${fieldId}-content`}>
                          {normalizedOptions.map((option) => (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                              data-testid={`${fieldId}-option-${String(option.value).replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }
                return (
                  <div key={field.key} className="space-y-2" data-testid={fieldId}>
                    <Label className="text-slate-200/80">{fieldLabel}</Label>
                    <Input
                      value={fieldValue}
                      onChange={(e) => updateValue(field.key, e.target.value)}
                      className="bg-white/5 ring-1 ring-white/10"
                      data-testid={`${fieldId}-input`}
                    />
                  </div>
                );
              }

              return (
                <div key={field.key} className="space-y-2" data-testid={fieldId}>
                  <Label className="text-slate-200/80">{fieldLabel}</Label>
                  <Input
                    value={fieldValue}
                    onChange={(e) => updateValue(field.key, e.target.value)}
                    className="bg-white/5 ring-1 ring-white/10"
                    inputMode={field.type === "currency" ? "decimal" : "text"}
                    data-testid={`${fieldId}-input`}
                  />
                </div>
              );
            })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="sold-dialog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || submitting}
            data-testid="sold-dialog-submit-button"
            onClick={async () => {
              setSubmitting(true);
              try {
                const soldPayload = Object.entries(formValues).reduce((acc, [key, value]) => {
                  const clean = pickFirstValue(value);
                  if (clean) acc[key] = key === "sold_option_index" ? Number(clean) : clean;
                  return acc;
                }, {});

                // P4-WP-14A: emit the authorization-bearing ids under their own
                // `_user_id` keys, and keep the human-readable names beside them
                // as DISPLAY METADATA only.
                //
                // The backend reads only the `_user_id` forms. The legacy
                // `sales_agent` / `split_agent` keys stay in the payload because
                // existing commission and reporting code reads them, but nothing
                // authorizing may consult them -- which is why the id is carried
                // separately rather than by redefining what those keys mean.
                const nameFor = (id) =>
                  salesAgents.find((agent) => agent.id === id)?.name || null;

                const salesAgentId = pickFirstValue(formValues.sales_agent) || null;
                if (salesAgentId) {
                  soldPayload.sales_agent_user_id = salesAgentId;
                  soldPayload.sales_agent_name = nameFor(salesAgentId) || user?.name || null;
                }

                const splitAgentId = pickFirstValue(formValues.split_agent) || null;
                if (splitAgentId) {
                  soldPayload.split_agent_user_id = splitAgentId;
                  soldPayload.split_agent_name = nameFor(splitAgentId);
                }
                if (quoteRef) {
                  soldPayload.quote_ref = quoteRef;
                  if (quoteRef.lead_id) soldPayload.lead_id = quoteRef.lead_id;
                  if (quoteRef.prospect_id) soldPayload.prospect_id = quoteRef.prospect_id;
                }
                await api.post("/clients", {
                  appointment_id: appointmentId,
                  sold_payload: soldPayload,
                  lead_id: quoteRef?.lead_id || undefined,
                  prospect_id: quoteRef?.prospect_id || undefined,
                });
                onSold?.(soldPayload);
                onOpenChange(false);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
